import type {
  CdwGridColumn,
  CdwGridSort,
  ExportPurpose,
  ExportQueryType,
} from '@cdw-ae/common-data';
import type { ExportDialogData } from './export-dialog.interfaces';

export interface ExportGridStateInputs {
  /** Column order from grid's getColumnOrder() */
  columnOrder: CdwGridColumn[];
  /** Sort order from grid's getSortOrder() */
  sortOrder: CdwGridSort[];
  /** Current active filters */
  filters: unknown[];
  /** Currently selected row IDs */
  selectedIds: string[];
  /** Total item count from grid's getItemTotal() */
  searchItemTotal: number;

  /** 'selected' or 'all' */
  exportScope: 'selected' | 'all';
  /** Export purpose (human-readable or import-ready) */
  purpose: ExportPurpose;
  /** Query type identifying the data source */
  queryType: ExportQueryType;

  customerId: number;
  /** Firebase UID (selection-context). NOT job tracking — see `dbUserId`. */
  fbUserId: string;
  dbUserId?: number;
  userEmail: string;
  /** Optional display name of the requesting user (used in export completion email) */
  userName?: string;
  /** File name prefix (e.g., "Devices Export") */
  exportName: string;

  /** Column order override for import-ready exports (optional) */
  importReadyColumnOrder?: CdwGridColumn[];
  /** Sort tiebreaker to append (e.g., DEVICE_SEARCH_SORT_TIEBREAKER) */
  sortTiebreaker: CdwGridSort;
}

/**
 * Build ExportDialogData from grid state inputs.
 * Reusable across any screen that has a CdwSearchGridComponent.
 *
 * Column order and sort order come from CdwSearchGridComponent's
 * getColumnOrder() and getSortOrder() public methods.
 */
export function buildExportDialogData(
  inputs: ExportGridStateInputs
): ExportDialogData {
  const {
    columnOrder,
    sortOrder,
    filters,
    selectedIds,
    searchItemTotal,
    exportScope,
    purpose,
    queryType,
    customerId,
    fbUserId,
    dbUserId,
    userEmail,
    userName,
    exportName,
    importReadyColumnOrder,
    sortTiebreaker,
  } = inputs;

  // Column order: human-readable uses grid's current order, import-ready uses template
  const resolvedColumnOrder =
    purpose === 'import-ready' && importReadyColumnOrder
      ? importReadyColumnOrder
      : columnOrder;

  // Sort order: grid's current sort + tiebreaker appended (deduplicated)
  const resolvedSortOrder = appendSortTiebreaker(sortOrder, sortTiebreaker);

  return {
    gridState: {
      columnOrder: resolvedColumnOrder,
      sortOrder: resolvedSortOrder,
      filters: filters as never[],
      selectedRowIds: selectedIds.length > 0 ? selectedIds : undefined,
    },
    totalItemCount: searchItemTotal,
    exportScope,
    selectedCount: selectedIds.length,
    purpose,
    queryType,
    customerId,
    fbUserId,
    dbUserId,
    userEmail,
    userName,
    exportName,
  };
}

function appendSortTiebreaker(
  sortOrder: CdwGridSort[],
  tiebreaker: CdwGridSort
): CdwGridSort[] {
  const allSorts = [...sortOrder, tiebreaker];
  const seen = new Set<string>();
  return allSorts.filter((s) => {
    if (seen.has(s.name)) return false;
    seen.add(s.name);
    return true;
  });
}
